import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Heart, Search, Download, Loader2, Phone, User, Home } from 'lucide-react';

export default function HeadLadiesPage() {
  const { api } = useAuth();
  const [headLadies, setHeadLadies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mohalla, setMohalla] = useState('');
  const [sector, setSector] = useState('');
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [exporting, setExporting] = useState(false);

  const fetchMohallas = useCallback(async () => {
    try {
      const response = await api.get('/mohallas');
      setMohallas(response.data.mohallas || []);
    } catch (error) {
      console.error('Failed to fetch mohallas:', error);
    }
  }, [api]);

  const fetchSectors = useCallback(async () => {
    try {
      const params = mohalla ? `?mohalla=${encodeURIComponent(mohalla)}` : '';
      const response = await api.get(`/sectors${params}`);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch sectors:', error);
    }
  }, [api, mohalla]);

  const fetchHeadLadies = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (mohalla) params.append('mohalla', mohalla);
      if (sector) params.append('sector', sector);
      params.append('page', page);
      params.append('limit', 50);

      const response = await api.get(`/dashboard/head-ladies?${params}`);
      setHeadLadies(response.data.head_ladies || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch head ladies');
    } finally {
      setLoading(false);
    }
  }, [api, search, mohalla, sector, page]);

  useEffect(() => {
    fetchMohallas();
  }, [fetchMohallas]);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors, mohalla]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchHeadLadies();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchHeadLadies]);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (mohalla) params.append('mohalla', mohalla);
      if (sector) params.append('sector', sector);
      params.append('format', format);

      const response = await api.get(`/dashboard/head-ladies/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `head_ladies.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getTypeBadgeColor = (type) => {
    if (type === 'Wife') return 'bg-pink-500';
    if (type?.includes('Misaq')) return 'bg-green-500';
    return 'bg-slate-500';
  };

  return (
    <div className="space-y-6" data-testid="head-ladies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            Head Ladies
          </h1>
          <p className="text-slate-500">Wife of HOF or Eldest Lady of each family</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => handleExport('excel')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleExport('pdf')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, ITS, mobile..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
              />
            </div>
            <Select value={mohalla} onValueChange={(v) => { setMohalla(v === 'all' ? '' : v); setSector(''); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Mohallas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mohallas</SelectItem>
                {mohallas.map((m) => (
                  <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sector} onValueChange={(v) => { setSector(v === 'all' ? '' : v); setPage(1); }} disabled={!mohalla}>
              <SelectTrigger>
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map((s) => (
                  <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center justify-end text-sm text-slate-500">
              Total: <strong className="ml-1">{total}</strong> families
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            </div>
          ) : headLadies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No head ladies found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ITS ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>HOF Name</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {headLadies.map((lady) => (
                    <TableRow key={lady.its_id || lady.id}>
                      <TableCell className="font-mono">{lady.its_id}</TableCell>
                      <TableCell className="font-medium">{lady.full_name}</TableCell>
                      <TableCell>
                        {lady.mobile && (
                          <a href={`tel:${lady.mobile}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                            <Phone className="w-3 h-3" />
                            {lady.mobile}
                          </a>
                        )}
                      </TableCell>
                      <TableCell>{lady.age || '-'}</TableCell>
                      <TableCell className="text-slate-600">{lady.hof_name}</TableCell>
                      <TableCell>{lady.mohalla}</TableCell>
                      <TableCell>{lady.sector}</TableCell>
                      <TableCell>
                        <Badge className={getTypeBadgeColor(lady.head_lady_type)}>
                          {lady.head_lady_type}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-slate-600">
            Page {page} of {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page === pages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
